/**
 * Proof of work upload (§13.5, §13.7: "camera-first, compress client-side").
 *
 * ┌─ THE ONE SCREEN THAT DECIDES WHETHER A VENDOR GETS PAID ──────────────┐
 * │ No payout is approved without proof. So this screen has to work at a   │
 * │ roadside site, on 3G, in sunlight, with one hand. Everything else is   │
 * │ negotiable; this is not.                                               │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * `capture="environment"` opens the rear camera directly rather than a file
 * picker. The photo is resized and re-encoded before it ever leaves the phone,
 * and for installation work the coordinates are captured alongside it because
 * the API refuses that proof without them.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type ActiveJob, type JobsResponse } from '../lib/api';
import { useI18n, capabilityLabel } from '../lib/i18n';
import { compressImage, getPosition } from '../lib/image';
import { formatDue } from '../lib/format';
import { ActionBar, Card, ErrorNote, Spinner, SubHeader } from '../components/Primitives';
import { Icon } from '../components/Icon';

type Stage = 'idle' | 'compressing' | 'locating' | 'uploading' | 'done';

const KIND_BY_CAPABILITY: Record<string, string> = {
  installation: 'photo',
  print_flex: 'challan',
  print_offset: 'challan',
  print_digital: 'challan',
  signage_fabrication: 'photo',
  newspaper: 'tearsheet',
  radio: 'log',
  voice_studio: 'audio',
};

export function ProofPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const fileInput = useRef<HTMLInputElement>(null);

  const [job, setJob] = useState<ActiveJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<string | null>(null);
  const [payload, setPayload] = useState<string | null>(null);
  const [bytes, setBytes] = useState(0);
  const [coords, setCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [stage, setStage] = useState<Stage>('idle');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const jobs = await api.get<JobsResponse>('/v1/partner/jobs');
      setJob(jobs.active.find((j) => j.jobId === jobId) ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
    } finally {
      setLoading(false);
    }
  }, [jobId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const needsGps = job?.capability === 'installation';

  async function onFile(file: File) {
    setError(null);
    setStage('compressing');
    try {
      const compressed = await compressImage(file);
      setPreview(compressed.dataUrl.startsWith('data:image') ? compressed.dataUrl : null);
      setPayload(compressed.dataUrl);
      setBytes(compressed.bytes);

      if (needsGps) {
        setStage('locating');
        // Asked for at the moment the photo is taken, not on page load: a
        // permission prompt before the vendor has done anything gets denied.
        const position = await getPosition();
        setCoords(position);
        if (!position) setError(t('proof.locationFailed'));
      }
      setStage('idle');
    } catch {
      setError(t('common.somethingWrong'));
      setStage('idle');
    }
  }

  async function submit() {
    if (!payload || !job) return;
    setStage('uploading');
    setError(null);

    try {
      const uploaded = await api.post<{ url: string }>('/v1/partner/uploads', {
        data: payload,
      });

      await api.post(`/v1/partner/jobs/${job.jobId}/proof`, {
        kind: KIND_BY_CAPABILITY[job.capability] ?? 'photo',
        fileUrl: uploaded.url,
        ...(coords ? { latitude: coords.latitude, longitude: coords.longitude } : {}),
        capturedAt: new Date().toISOString(),
      });

      setStage('done');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
      setStage('idle');
    }
  }

  if (loading) return <Spinner />;
  if (!job)
    return <ErrorNote message={t('common.somethingWrong')} onRetry={() => navigate('/jobs')} />;

  if (stage === 'done') {
    return (
      <div className="py-10 text-center">
        <span
          aria-hidden
          className="mx-auto flex h-20 w-20 items-center justify-center rounded-[28px] text-white shadow-lift"
          style={{ backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }}
        >
          <Icon name="check" className="h-10 w-10" strokeWidth={2.4} />
        </span>
        <h1 className="mt-5 font-display text-2xl font-bold">{t('proof.sent')}</h1>
        <p className="mx-auto mt-2 max-w-xs text-sm text-muted">{t('proof.paidAfter')}</p>
        <button type="button" className="btn-primary mt-8" onClick={() => navigate('/jobs')}>
          {t('nav.jobs')}
        </button>
      </div>
    );
  }

  const busy = stage === 'compressing' || stage === 'locating' || stage === 'uploading';

  return (
    <div>
      <SubHeader
        back="/jobs"
        icon="camera"
        title={t('proof.title')}
        sub={`${job.title} · ${capabilityLabel(job.capability, lang)} · ${formatDue(job.neededBy, lang)}`}
      />

      <Card className="mt-4">
        <p className="field-label">{t('jobs.proofNeeded')}</p>
        <p className="text-sm">{job.proofRequired}</p>
        {needsGps && <p className="mt-2 text-xs text-live">{t('proof.gpsNeeded')}</p>}
      </Card>

      <input
        ref={fileInput}
        type="file"
        // Rear camera straight away. A vendor at the site should not be sent
        // to a gallery to find a photo they have not taken yet.
        accept="image/*,application/pdf,audio/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onFile(file);
        }}
      />

      <div className="mt-4 space-y-3">
        <button
          type="button"
          className="btn-secondary min-h-[64px]"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
        >
          📷 {preview || payload ? t('proof.chooseFile') : t('proof.takePhoto')}
        </button>

        {preview && (
          <figure className="overflow-hidden rounded-card border border-line">
            <img src={preview} alt="" className="max-h-72 w-full object-cover" />
            <figcaption className="tnum bg-panel-2 px-3 py-2 text-xs text-muted">
              {Math.round(bytes / 1024)} KB
              {coords && ` · ${t('proof.locationOk')}`}
            </figcaption>
          </figure>
        )}

        {!preview && payload && (
          <p className="rounded-xl bg-panel-2 p-3 text-sm text-muted">
            {Math.round(bytes / 1024)} KB
          </p>
        )}

        {stage === 'compressing' && (
          <p className="text-sm text-muted">{t('proof.compressing')}</p>
        )}
        {stage === 'locating' && (
          <p className="text-sm text-muted">{t('proof.gettingLocation')}</p>
        )}
        {error && <p className="text-sm text-bad">{error}</p>}
      </div>

      <ActionBar>
        <button
          type="button"
          className="btn-primary"
          onClick={() => void submit()}
          // Installation proof cannot be sent without coordinates: the API
          // refuses it, so blocking here saves a round trip and a confusing
          // error the vendor cannot act on from this screen.
          disabled={busy || !payload || (needsGps && !coords)}
        >
          {stage === 'uploading' ? t('proof.uploading') : t('proof.send')}
        </button>
      </ActionBar>
    </div>
  );
}
